import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import api from '@/lib/api/axios'
import { useCan } from '@/hooks/useCan'

export interface OptionVocabGroup {
    id: number
    name: string
    description: string | null
    options: { id: number; name: string; description: string | null; deriveMode: 'automated' | 'manual' | null }[]
}

/**
 * The branch a task belongs to, when the group's branches ARE the automation choice.
 *
 * For a group like recover-vm the two paths are "a script does it" and "an engineer does
 * it" — which is what Automation already says, so asking again is asking the same question
 * twice. Groups whose branches are a genuine technique choice (PXE vs USB, mksysb vs
 * alt_disk) carry no deriveMode and are still chosen by hand.
 */
export function deriveOptionFromMode(group: OptionVocabGroup | undefined, mode: string | null | undefined): string | null {
    if (!group) return null
    const wanted = (mode || 'manual').toLowerCase() === 'manual' ? 'manual' : 'automated'
    return group.options.find((o) => o.deriveMode === wanted)?.name ?? null
}

/** Does this group's branch follow Automation rather than being picked? */
export function isDerivedGroup(group: OptionVocabGroup | undefined): boolean {
    return !!group?.options.some((o) => o.deriveMode)
}

const NONE = '__none__'
const ADD = '__add__'

/**
 * The shared vocabulary, cached once per session — it is small and changes rarely.
 *
 * Asked only by a role holding master-runbooks.read, which the route checks: every runbook table
 * mounts this, including the read-only views an engineer opens, and without the permission the
 * answer is a 403 rather than a list. Without it the vocabulary is empty, as it is while loading.
 */
export function useOptionVocabulary() {
    const canRead = useCan('master-runbooks.read')
    return useQuery<OptionVocabGroup[]>({
        queryKey: ['runbook-option-vocabulary'],
        queryFn: async () => (await api.get('/api/runbook-options')).data?.data?.groups ?? [],
        staleTime: 5 * 60 * 1000,
        enabled: canRead,
    })
}

/**
 * Option group and option, chosen from a shared list rather than typed.
 *
 * Typing them meant the same words were retyped in every runbook, and a near miss —
 * "restore-vm" against "Restore-VM" — silently produced two groups, each rendering as a
 * decision with one path, which is no decision at all. The list is extensible from here,
 * because a vocabulary you cannot add to just gets worked around with a near-miss name.
 *
 * A value already on a task that is not in the list is still offered, so tags written
 * before this existed keep working and cannot be lost by opening the editor.
 */
export function OptionGroupPicker({
    group,
    option,
    mode,
    onChange,
    disabled = false,
}: {
    group: string | null | undefined
    option: string | null | undefined
    /** The task's Automation — decides the branch for groups that derive it. */
    mode?: string | null
    onChange: (next: { optionGroup: string | null; optionName: string | null }) => void
    disabled?: boolean
}) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const { data: groups = [] } = useOptionVocabulary()

    const [addingGroup, setAddingGroup] = useState(false)
    const [addingOption, setAddingOption] = useState(false)
    const [draft, setDraft] = useState('')

    const currentGroup = (group || '').trim()
    const currentOption = (option || '').trim()
    const known = groups.find((g) => g.name === currentGroup)
    const derived = isDerivedGroup(known)

    const groupNames = groups.map((g) => g.name)
    if (currentGroup && !groupNames.includes(currentGroup)) groupNames.push(currentGroup)

    const optionNames = (known?.options ?? []).map((o) => o.name)
    if (currentOption && !optionNames.includes(currentOption)) optionNames.push(currentOption)

    const addGroup = useMutation({
        mutationFn: (name: string) => api.post('/api/runbook-options', { name }),
        onSuccess: (_res, name) => {
            queryClient.invalidateQueries({ queryKey: ['runbook-option-vocabulary'] })
            onChange({ optionGroup: name, optionName: null })
            setAddingGroup(false)
            setDraft('')
        },
        onError: (e: any) => toast({ title: 'Could not add the group', description: e?.response?.data?.message, variant: 'destructive' }),
    })

    const addOption = useMutation({
        mutationFn: (name: string) => api.post(`/api/runbook-options/${known?.id}/values`, { name }),
        onSuccess: (_res, name) => {
            queryClient.invalidateQueries({ queryKey: ['runbook-option-vocabulary'] })
            onChange({ optionGroup: currentGroup, optionName: name })
            setAddingOption(false)
            setDraft('')
        },
        onError: (e: any) => toast({ title: 'Could not add the option', description: e?.response?.data?.message, variant: 'destructive' }),
    })

    const inlineAdd = (onCommit: (v: string) => void, onCancel: () => void, placeholder: string) => (
        <div className="flex items-center gap-1">
            <Input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && draft.trim()) { e.preventDefault(); onCommit(draft.trim()) }
                    if (e.key === 'Escape') { e.preventDefault(); onCancel(); setDraft('') }
                }}
                placeholder={placeholder}
                className="h-8 w-[150px] text-sm"
            />
            <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0"
                disabled={!draft.trim()} onClick={() => onCommit(draft.trim())}>
                <Check className="h-4 w-4" />
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0"
                onClick={() => { onCancel(); setDraft('') }}>
                <X className="h-4 w-4" />
            </Button>
        </div>
    )

    return (
        <>
            <div>
                <label className="text-xs text-gray-500 block mb-1">
                    {t('objects.runbook.optionGroup', 'Option group')}
                </label>
                {addingGroup
                    ? inlineAdd((v) => addGroup.mutate(v), () => setAddingGroup(false), t('objects.runbook.newOptionGroup', 'New group name'))
                    : (
                        <Select
                            value={currentGroup || NONE}
                            disabled={disabled}
                            onValueChange={(v) => {
                                if (v === ADD) { setAddingGroup(true); return }
                                if (v === NONE) { onChange({ optionGroup: null, optionName: null }); return }
                                // Changing group invalidates the option: branch names belong to a group.
                                // If the new group derives its branch from Automation, fill it in — the
                                // author has already answered that question in the Automation field.
                                const next = groups.find((g) => g.name === v)
                                const auto = deriveOptionFromMode(next, mode)
                                onChange({
                                    optionGroup: v,
                                    optionName: auto ?? (v === currentGroup ? currentOption || null : null),
                                })
                            }}
                        >
                            <SelectTrigger className="w-[190px] h-8 text-sm">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={NONE}>
                                    <span className="text-muted-foreground">{t('objects.runbook.noOptionGroup', 'None')}</span>
                                </SelectItem>
                                {groupNames.map((name) => (
                                    <SelectItem key={name} value={name}>{name}</SelectItem>
                                ))}
                                <SelectItem value={ADD}>
                                    <span className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
                                        <Plus className="h-3.5 w-3.5" />
                                        {t('objects.runbook.addOptionGroup', 'Add a group…')}
                                    </span>
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    )}
            </div>

            <div>
                <label className="text-xs text-gray-500 block mb-1">
                    {t('objects.runbook.optionName', 'Option')}
                </label>
                {addingOption
                    ? inlineAdd((v) => addOption.mutate(v), () => setAddingOption(false), t('objects.runbook.newOption', 'New option name'))
                    : (
                        <Select
                            value={currentOption || NONE}
                            disabled={disabled || !currentGroup || derived}
                            onValueChange={(v) => {
                                // Only a known group can take a new option — there is nothing to add it to otherwise.
                                if (v === ADD) { if (known) setAddingOption(true); return }
                                onChange({ optionGroup: currentGroup || null, optionName: v === NONE ? null : v })
                            }}
                        >
                            <SelectTrigger className="w-[150px] h-8 text-sm">
                                <SelectValue placeholder={t('objects.runbook.noOptionGroup', 'None')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={NONE}>
                                    <span className="text-muted-foreground">{t('objects.runbook.noOptionGroup', 'None')}</span>
                                </SelectItem>
                                {optionNames.map((name) => (
                                    <SelectItem key={name} value={name}>{name}</SelectItem>
                                ))}
                                {known && (
                                    <SelectItem value={ADD}>
                                        <span className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
                                            <Plus className="h-3.5 w-3.5" />
                                            {t('objects.runbook.addOption', 'Add an option…')}
                                        </span>
                                    </SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                    )}
                {derived && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                        {t('objects.runbook.optionFollowsAutomation', 'follows Automation')}
                    </p>
                )}
            </div>
        </>
    )
}
